import { useState, useEffect, useRef, useMemo } from "react";
import axios from "axios";
import {
  UploadCloud, Send, Search, Trash2, ExternalLink, Loader2, Film, Inbox
} from "lucide-react";
import { toast } from "sonner";
import {
  SOCIAL_TYPES, CLIENT_TYPES, TYPE_META, shortBranch, formatSize, formatAssetDate
} from "./SocialManagerPortal";
import "@/SocialManagerPortal.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const CHUNK_SIZE = 6 * 1024 * 1024;
const ALL_TYPES = [...SOCIAL_TYPES, ...CLIENT_TYPES];

function EditorSocialUploads() {
  const [branches, setBranches] = useState([]);
  const [assets, setAssets] = useState([]);
  const [form, setForm] = useState({ branch_id: "", asset_type: "sneak_peek", title: "" });
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [search, setSearch] = useState("");
  const fileRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const [bRes, aRes] = await Promise.all([
          axios.get(`${API}/social-assets/my-branches`),
          axios.get(`${API}/social-assets`),
        ]);
        setBranches(bRes.data.branches || []);
        setAssets(aRes.data.assets || []);
      } catch {
        toast.error("Failed to load Social Library data");
      }
    })();
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return assets;
    return assets.filter(a => `${a.title} ${a.branch_name} ${a.filename}`.toLowerCase().includes(q));
  }, [assets, search]);

  const handleUpload = async () => {
    if (!form.branch_id) return toast.error("Pick a branch");
    if (!form.title.trim()) return toast.error("Add the property address / title");
    if (!file) return toast.error("Choose a video file");

    setUploading(true);
    setProgress(0);
    try {
      const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
      const initFd = new FormData();
      initFd.append("branch_id", form.branch_id);
      initFd.append("asset_type", form.asset_type);
      initFd.append("title", form.title.trim());
      initFd.append("filename", file.name);
      initFd.append("total_chunks", totalChunks);
      initFd.append("total_size", file.size);
      const initRes = await axios.post(`${API}/social-assets/init-upload`, initFd);
      const uploadId = initRes.data.upload_id;

      for (let i = 0; i < totalChunks; i++) {
        const blob = file.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);
        const fd = new FormData();
        fd.append("upload_id", uploadId);
        fd.append("chunk_number", i);
        fd.append("chunk", blob, `chunk_${i}`);
        await axios.post(`${API}/social-assets/upload-chunk`, fd);
        setProgress(Math.round(((i + 1) / totalChunks) * 92));
      }

      const doneFd = new FormData();
      doneFd.append("upload_id", uploadId);
      const doneRes = await axios.post(`${API}/social-assets/complete-upload`, doneFd, { timeout: 180000 });
      setProgress(100);
      setAssets(prev => [doneRes.data.asset, ...prev]);
      toast.success("Pushed to the Social Library");
      setForm(f => ({ ...f, title: "" }));
      setFile(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed — please try again");
    } finally {
      setUploading(false);
      setTimeout(() => setProgress(0), 1500);
    }
  };

  const handleDelete = async (asset) => {
    if (!window.confirm(`Delete "${asset.title || asset.filename}" from the Social Library?`)) return;
    try {
      await axios.delete(`${API}/social-assets/${asset.id}`);
      setAssets(prev => prev.filter(a => a.id !== asset.id));
      toast.success("Asset deleted");
    } catch {
      toast.error("Failed to delete asset");
    }
  };

  return (
    <div className="esu-wrap" data-testid="editor-social-uploads">
      <div className="esu-form-card">
        <h3><Send size={17} /> Push to Social Library</h3>
        <p className="esu-form-sub">
          Upload a finished video so the branch's social media manager can preview and download it.
        </p>

        <div className="esu-field">
          <label>Branch</label>
          <select
            value={form.branch_id}
            onChange={(e) => setForm(f => ({ ...f, branch_id: e.target.value }))}
            data-testid="esu-branch-select"
          >
            <option value="">Select branch…</option>
            {branches.map(b => (
              <option key={b.id} value={b.id}>{b.name}</option>
            ))}
          </select>
        </div>

        <div className="esu-field">
          <label>Asset type</label>
          <div className="esu-type-pills">
            {ALL_TYPES.map(t => (
              <button
                key={t.key}
                type="button"
                className={`esu-type-pill ${form.asset_type === t.key ? "active" : ""}`}
                style={form.asset_type === t.key ? { "--pill-accent": t.color } : undefined}
                onClick={() => setForm(f => ({ ...f, asset_type: t.key }))}
                data-testid={`esu-type-${t.key}`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        <div className="esu-field">
          <label>Property address / title</label>
          <input
            type="text"
            placeholder="e.g. 14 Castle Street, Farnham"
            value={form.title}
            onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))}
            data-testid="esu-title-input"
          />
        </div>

        <div
          className={`esu-dropzone ${file ? "has-file" : ""}`}
          onClick={() => fileRef.current?.click()}
          data-testid="esu-dropzone"
        >
          <UploadCloud size={26} />
          {file ? (
            <div>
              <div className="esu-file-name">{file.name}</div>
              <div>{formatSize(file.size)}</div>
            </div>
          ) : (
            <div>Click to choose a video file</div>
          )}
          <input
            ref={fileRef}
            type="file"
            accept="video/*"
            style={{ display: "none" }}
            onChange={(e) => { setFile(e.target.files?.[0] || null); e.target.value = ""; }}
            data-testid="esu-file-input"
          />
        </div>

        {uploading && (
          <>
            <div className="esu-progress-track">
              <div className="esu-progress-fill" style={{ width: `${progress}%` }} />
            </div>
            <div className="esu-progress-label" data-testid="esu-progress">
              {progress < 92 ? `Uploading… ${progress}%` : progress < 100 ? "Finalising…" : "Done"}
            </div>
          </>
        )}

        <button
          className="esu-submit-btn"
          onClick={handleUpload}
          disabled={uploading}
          data-testid="esu-submit-btn"
        >
          {uploading ? <><Loader2 size={16} className="smp-spin" /> Uploading…</> : <><Send size={16} /> Submit to Social Library</>}
        </button>
      </div>

      <div className="esu-list-card">
        <div className="esu-list-head">
          <h3><Film size={17} /> In the Hub ({assets.length})</h3>
          <div className="esu-list-search">
            <Search size={14} />
            <input
              placeholder="Search assets…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="esu-search-input"
            />
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="esu-empty" data-testid="esu-empty">
            <Inbox size={30} />
            <div>No assets uploaded yet — push your first one through.</div>
          </div>
        ) : (
          <div className="esu-rows" data-testid="esu-asset-list">
            {filtered.map(a => {
              const meta = TYPE_META[a.asset_type] || { label: a.asset_type, color: "#94a3b8" };
              return (
                <div key={a.id} className="esu-row" data-testid={`esu-asset-row-${a.id}`}>
                  {a.thumb_url ? (
                    <img src={a.thumb_url} alt="" className="esu-row-thumb" loading="lazy" />
                  ) : (
                    <div className="esu-row-thumb-fallback"><Film size={18} /></div>
                  )}
                  <div className="esu-row-main">
                    <p className="esu-row-title">{a.title || a.filename}</p>
                    <div className="esu-row-meta">
                      <span>{shortBranch(a.branch_name)}</span>
                      <span>•</span>
                      <span>{formatAssetDate(a.created_at)}</span>
                      {a.size ? <><span>•</span><span>{formatSize(a.size)}</span></> : null}
                      {a.uploaded_by_name ? <><span>•</span><span>by {a.uploaded_by_name}</span></> : null}
                    </div>
                  </div>
                  <span className="esu-row-type" style={{ "--badge-color": meta.color }}>{meta.label}</span>
                  <div className="esu-row-actions">
                    <button
                      className="esu-icon-btn"
                      title="Preview"
                      onClick={() => window.open(a.url, "_blank")}
                      data-testid={`esu-preview-${a.id}`}
                    >
                      <ExternalLink size={14} />
                    </button>
                    <button
                      className="esu-icon-btn danger"
                      title="Delete"
                      onClick={() => handleDelete(a)}
                      data-testid={`esu-delete-${a.id}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default EditorSocialUploads;
